import type { JSX } from 'react';
import { useState } from 'react';
import { Button, Icon, Label, Select, SelectOption, useToast } from '@wistia/ui';
import type { MediaLocalization } from '~/graphql';
import { isNonEmptyString } from '~/utilities/type-guards';
import { trackStructEvent } from '~/utilities/snowplow';
import { SelectWrapper, InlineSelectWrapper } from './SharedStyledComponents';

type Props = {
  localizationFiles: MediaLocalization[];
};

export const LocalizationDownload = ({ localizationFiles }: Props): JSX.Element => {
  const showToast = useToast();
  const [selectedLanguageTag, setSelectedLanguageTag] = useState(
    localizationFiles[0]?.ietfLanguageTag ?? '',
  );

  const onChange = (value: string) => {
    setSelectedLanguageTag(value);
  };

  const selectedLanguage = localizationFiles.find(
    ({ ietfLanguageTag }) => ietfLanguageTag === selectedLanguageTag,
  );

  const handleDownload = () => {
    const downloadUrl = selectedLanguage?.downloadUrl ?? '';

    if (isNonEmptyString(downloadUrl)) {
      window.open(downloadUrl, '_blank');

      trackStructEvent({
        category: 'button',
        action: 'click',
        label: 'publish-download-audio-files-for-video',
      });
    } else {
      showToast({
        message: 'Sorry, this file is not available for download yet. Please try again later.',
        colorScheme: 'error',
      });
    }
  };

  return (
    <SelectWrapper>
      <Label>Multi-language tracks</Label>
      <InlineSelectWrapper>
        <Select
          fullWidth={true}
          onChange={onChange}
          value={selectedLanguageTag}
        >
          {localizationFiles.map(({ ietfLanguageTag, languageName, nativeLanguageName }) => (
            <SelectOption
              key={ietfLanguageTag}
              value={ietfLanguageTag}
            >
              {languageName} {nativeLanguageName !== languageName && `(${nativeLanguageName})`}
            </SelectOption>
          ))}
        </Select>

        <Button
          onClick={handleDownload}
          leftIcon={<Icon type="download" />}
          data-testid="download-localization"
        >
          Download
        </Button>
      </InlineSelectWrapper>
    </SelectWrapper>
  );
};
